import _ from 'lodash';
import State from '@wfp-root-app/store/state';
import { routerHelperActions } from '@wfp-common/store/routerHelperSlice';

import Action from '../action';
import {
    getManager,
    getMyself,
    getPendingActions,
    authLogout,
    authRefresh,
    requestPasswordReset as requestPasswordResetApi,
} from '../apiClient';
import Manager from '../managers/manager';
import { ActionTypes as SessionProlongActionTypes } from '../utils/sessionExpiration/sessionExpiration';
import { errorAction } from '../utils/notifications';
import { ManagerPermission } from '../permission-profiles/permission';
import { windowStorage, WindowStorageToken } from '../windowStorage';
import { stopManagerTokenExpirationChecks } from '../utils/sessionExpiration/scheduleManagerTokenExpirationChecks';

export class AuthState {
    apiToken?: string;
    manager?: Manager;
    error?: unknown;

    constructor(apiToken: string = null, manager: Manager = null, error: unknown = null) {
        this.apiToken = apiToken || windowStorage.getItem(WindowStorageToken.managerApiToken);
        this.manager = manager;
        this.error = error;
    }
}

export function hasFeatureAccess(auth: AuthState, feature: ManagerPermission) {
    if (auth.manager?.profile?.permissions) {
        return auth.manager.profile.permissions.includes(feature);
    }
    return false;
}

export function hasOneOfPermissions(auth: AuthState, features: ManagerPermission[]) {
    if (auth.manager?.profile?.permissions) {
        const commonFeatures = _.intersection(auth.manager.profile.permissions, features);
        return commonFeatures.length > 0;
    }
    return false;
}

export function doesManagerPermissionsIncludeGivenFeatures(features: ManagerPermission[], manager: Manager) {
    return manager && _.intersection(manager.profile?.permissions, features).length === features.length;
}

export const ActionTypes = {
    loginSuccessful: 'LoginPage.login',
    loginFailed: 'LoginPage.loginFailed',
    logoutSuccessful: 'LoginPage.logout',
    selfEdit: 'LoginPage.selfEdit',
    reloadUserSuccess: 'LoginPage.reloadUserSuccess',
    loadedPendingActions: 'LoginPage.loadedPendingActions',
    loadedSelf: 'LoginPage.loadedSelf',
};

export const ActionCreators = {
    getMyself() {
        return (dispatch) => {
            return getMyself()
                .then((res) => {
                    dispatch({
                        type: ActionTypes.loadedSelf,
                        payload: res.manager,
                    });
                    dispatch(routerHelperActions.makeRedirect('/home'));
                })
                .catch(() => {
                    dispatch(authLogout());
                });
        };
    },
    refreshToken() {
        return async (dispatch) => {
            return authRefresh()
                .then((response) => {
                    if (!response.access_token) {
                        dispatch(errorAction('Your session has expired.'));
                        dispatch(ActionCreators.logout());
                        return;
                    }
                    windowStorage.setItem(WindowStorageToken.managerApiToken, response.access_token);
                    dispatch({
                        type: SessionProlongActionTypes.newToken,
                        payload: {
                            apiToken: response.access_token,
                        },
                    });
                })
                .catch(() => {
                    dispatch(errorAction('Your session has expired.'));
                    dispatch(ActionCreators.logout());
                });
        };
    },

    logout() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return async (dispatch: any) => {
            stopManagerTokenExpirationChecks();
            try {
                const res = await authLogout();
                if (res.logout_url) {
                    windowStorage.removeItem(WindowStorageToken.managerApiToken);
                    window.location.replace(res.logout_url);
                }
            } catch (e) {
                dispatch(errorAction('Logout failed'));
            }
        };
    },
    reloadUserData() {
        return (dispatch, getState: () => State) => {
            const manager = getState().auth.manager;
            if (manager) {
                return getManager(manager.id).then((manager) => {
                    dispatch({
                        type: ActionTypes.reloadUserSuccess,
                        payload: { manager },
                    });
                    dispatch(ActionCreators.getPendingActions());
                });
            }
        };
    },
    getPendingActions() {
        return (dispatch, getState: () => State) =>
            getPendingActions().then((pendingActions) => {
                if (getState().auth.manager) {
                    const permissions = getState().auth.manager.profile?.permissions;
                    dispatch({
                        type: ActionTypes.loadedPendingActions,
                        payload: Object.assign({}, { pendingActions }, { permissions }),
                    });
                }
            });
    },
    requestPasswordReset() {
        return async () => {
            const res = await requestPasswordResetApi();
            if (res && res.resetLink) {
                window.location.replace(res.resetLink);
            }
        };
    },
};

export function authReducer(state: AuthState = new AuthState(), action: Action) {
    switch (action.type) {
        case ActionTypes.loginSuccessful:
            return new AuthState(action.payload.apiToken, action.payload.manager);
        case ActionTypes.loginFailed:
            return new AuthState(null, null, action.payload.error);
        case ActionTypes.logoutSuccessful:
            return new AuthState();
        case SessionProlongActionTypes.newToken:
            return new AuthState(action.payload.apiToken, state.manager);
        case ActionTypes.selfEdit:
            return new AuthState(state.apiToken, action.payload.manager);
        case ActionTypes.reloadUserSuccess:
            return new AuthState(state.apiToken, action.payload.manager);
        case ActionTypes.loadedSelf:
            return new AuthState(state.apiToken, action.payload);
    }

    return state;
}
